import { createAndInjectScript } from '@/utilityFunctions'

const GOOGLE_MAPS_API_NAME = 'The Google Maps JavaScript API'
const PLACEHOLDER_VALUE_PATTERN = /^__.+__$/
const MISSING_CONFIG_ERROR =
  'Google Maps loader is not configured. Provide VUE_APP_GOOGLE_MAPS_SCRIPT_URL and either VUE_APP_GOOGLE_MAPS_API_KEY (or VUE_APP_GOOGLE_MAPS_SCRIPT_KEY as fallback) or VUE_APP_GOOGLE_MAPS_AUTH.'

export function hasConfiguredValue(value) {
  return (
    typeof value === 'string' &&
    !!value.trim() &&
    !PLACEHOLDER_VALUE_PATTERN.test(value.trim())
  )
}

export function resolveGoogleMapsApiKey(env = process.env, useNewKey = true) {
  const apiKeyCandidates = useNewKey
    ? [env.VUE_APP_GOOGLE_MAPS_API_KEY, env.VUE_APP_GOOGLE_MAPS_SCRIPT_KEY]
    : [env.VUE_APP_GOOGLE_MAPS_SCRIPT_KEY]

  return apiKeyCandidates.find((value) => hasConfiguredValue(value)) || ''
}

export function parseAuthQueryParams(authString = '') {
  if (!hasConfiguredValue(authString)) {
    return {}
  }

  const normalizedAuthString = authString.startsWith('?')
    ? authString.slice(1)
    : authString
  const authParams = new URLSearchParams(normalizedAuthString)

  return [...authParams.entries()].reduce((params, [key, value]) => {
    if (hasConfiguredValue(value)) {
      params[key] = value
    }

    return params
  }, {})
}

export function getGoogleMapsLoaderOptions(env = process.env, store = null) {
  const scriptUrl = hasConfiguredValue(env.VUE_APP_GOOGLE_MAPS_SCRIPT_URL)
    ? env.VUE_APP_GOOGLE_MAPS_SCRIPT_URL
    : ''
  const isApiKeyEnabled =
    store?.getters?.featureFlags?.isGoogleMapsApiKeyEnabled ?? false

  if (!isApiKeyEnabled) {
    return {
      scriptUrl,
      authMode: 'legacy',
      loaderParams: {
        ...parseAuthQueryParams(env.VUE_APP_GOOGLE_MAPS_AUTH),
        v: 'weekly',
      },
    }
  }
  const apiKey = resolveGoogleMapsApiKey(env, true)

  return {
    scriptUrl,
    authMode: 'apiKey',
    loaderParams: {
      key: apiKey,
      v: 'weekly',
    },
  }
}

function toGoogleMapsQueryParamName(key) {
  return key.replace(/[A-Z]/g, (letter) => `_${letter.toLowerCase()}`)
}

function createMissingConfigImporter() {
  return () => Promise.reject(new Error(MISSING_CONFIG_ERROR))
}

export function initializeGoogleMaps(
  windowRef = window,
  documentRef = document,
  env = process.env,
  store = null,
) {
  const googleRef = windowRef.google || (windowRef.google = {})
  const mapsRef = googleRef.maps || (googleRef.maps = {})

  if (mapsRef.importLibrary) {
    return mapsRef.importLibrary
  }

  const { scriptUrl, loaderParams, authMode } = getGoogleMapsLoaderOptions(
    env,
    store,
  )

  const hasApiKeyConfig = hasConfiguredValue(loaderParams.key)
  const hasLegacyAuthConfig =
    authMode === 'legacy' && Object.keys(loaderParams).length > 0

  if (!scriptUrl || (!hasApiKeyConfig && !hasLegacyAuthConfig)) {
    console.error('[GoogleMaps] Missing required config!', {
      scriptUrl: !!scriptUrl,
      hasApiKeyConfig,
      hasLegacyAuthConfig,
    })
    mapsRef.importLibrary = createMissingConfigImporter()
    return mapsRef.importLibrary
  }

  let scriptPromise = null
  const requestedLibraries = new Set()

  const loadScript = () => {
    if (scriptPromise) {
      return scriptPromise
    }

    scriptPromise = new Promise((resolve, reject) => {
      const queryParams = new URLSearchParams()
      queryParams.set('libraries', [...requestedLibraries].join(','))

      Object.keys(loaderParams).forEach((key) => {
        queryParams.set(
          toGoogleMapsQueryParamName(key),
          loaderParams[key],
        )
      })

      queryParams.set('callback', 'google.maps.__ib__')

      const src = `${scriptUrl}?${queryParams.toString()}`
      mapsRef.__ib__ = resolve

      const nonceScript = documentRef.querySelector('script[nonce]')
      const nonce = nonceScript ? nonceScript.nonce : ''

      createAndInjectScript({
        src,
        nonce,
        documentRef,
        onError: () => {
          console.error('[GoogleMaps] Script load failed')
          scriptPromise = null
          reject(new Error(`${GOOGLE_MAPS_API_NAME} could not load.`))
        },
      })
    })

    return scriptPromise
  }

  mapsRef.importLibrary = (libraryName, ...args) => {
    requestedLibraries.add(libraryName)
    return loadScript().then(() => mapsRef.importLibrary(libraryName, ...args))
  }

  return mapsRef.importLibrary
}
